import { useState, useEffect } from 'react'
import { AdminLayout } from '../../components/layouts/Layout'
import { Card, Button, Input, Textarea } from '../../components/common/UI'
import { Send, Users, Target } from 'lucide-react'
import { notificationService } from '../../services/notifications'
import { userService } from '../../services/api'
import { OPTIONS } from '../../utils/constants'

export default function Notifications() {
  const [users, setUsers] = useState<any[]>([])
  const [formData, setFormData] = useState({
    targetType: 'ALL' as 'ALL' | 'LOCATION' | 'INTERESTS' | 'CUSTOM',
    locations: [] as string[],
    interests: [] as string[],
    title: '',
    body: ''
  })
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async () => {
    try {
      const data = await userService.list()
      setUsers(data)
    } catch (error) {
      console.error('Failed to load users:', error)
    }
  }

  const handleSend = async () => {
    if (!formData.title || !formData.body) return

    setLoading(true)
    try {
      await notificationService.sendBulkNotification({
        targetType: formData.targetType,
        locations: formData.locations,
        interests: formData.interests,
        title: formData.title,
        body: formData.body
      })
      setSuccess(true)
      setFormData({
        targetType: 'ALL',
        locations: [],
        interests: [],
        title: '',
        body: ''
      })
      setTimeout(() => setSuccess(false), 3000)
    } catch (error) {
      console.error('Failed to send notification:', error)
    } finally {
      setLoading(false)
    }
  }

  const getTargetCount = () => {
    if (formData.targetType === 'ALL') {
      return users.filter(u => !u.isDeactivated).length
    } else if (formData.targetType === 'INTERESTS') {
      return users.filter(u => 
        !u.isDeactivated && 
        u.interests?.some((i: string) => formData.interests.includes(i))
      ).length
    }
    return 0
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Send Notifications</h1>
          <p className="text-gray-600">Broadcast messages to app users</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <h3 className="text-lg font-semibold mb-4">Notification Content</h3>
            <div className="space-y-4">
              <Input
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter notification title"
              />

              <Textarea
                label="Message"
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                rows={6}
                placeholder="Enter notification message"
              />

              <div>
                <label className="block text-sm font-medium mb-2">Target Audience</label>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { value: 'ALL', label: 'All Users', icon: Users },
                    { value: 'INTERESTS', label: 'By Interests', icon: Target }
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, targetType: option.value as any })}
                      className={`p-4 border-2 rounded-lg flex items-center space-x-3 transition-colors ${
                        formData.targetType === option.value
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <option.icon className="w-5 h-5" />
                      <span className="font-medium">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {formData.targetType === 'INTERESTS' && (
                <div>
                  <label className="block text-sm font-medium mb-2">Select Interests</label>
                  <div className="grid grid-cols-3 gap-2">
                    {OPTIONS.interests.map((interest) => (
                      <button
                        key={interest}
                        type="button"
                        onClick={() => {
                          const newInterests = formData.interests.includes(interest)
                            ? formData.interests.filter(i => i !== interest)
                            : [...formData.interests, interest]
                          setFormData({ ...formData, interests: newInterests })
                        }}
                        className={`p-2 text-sm border rounded-lg transition-colors ${
                          formData.interests.includes(interest)
                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        {interest}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={handleSend}
                loading={loading}
                disabled={!formData.title || !formData.body}
                icon={Send}
                className="w-full"
              >
                Send Notification
              </Button>

              {success && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-600">
                  Notification sent successfully!
                </div>
              )}
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold mb-4">Preview & Stats</h3>
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Target Audience</p>
                <p className="text-2xl font-bold">{getTargetCount()} users</p>
              </div>

              <div className="p-4 border-2 border-dashed rounded-lg">
                <p className="text-xs text-gray-500 mb-2">NOTIFICATION PREVIEW</p>
                <div className="bg-white p-3 rounded shadow-sm">
                  <p className="font-semibold text-sm">{formData.title || 'Title'}</p>
                  <p className="text-xs text-gray-600 mt-1">{formData.body || 'Message'}</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </AdminLayout>
  )
}